import type { AskShape, CurrencyState, DraftPart, DraftPartKind, GraphEdge, GraphNode, LintFlag, LintKind, PartOrigin, Provenance } from '../types/workbench'

// Words and pure helpers for the workbench. No React, no fetch. The API's vocabulary never shows bare:
// a rep reads "two dates and a coffee", not "two-dates-plus-format".

export const PART_WORDS: Record<DraftPartKind, string> = {
  subject: 'Subject', hook: 'The opener', insight: 'The insight', ask: 'The ask', signoff: 'Sign-off', body: 'Body',
}

export const LINT_WORDS: Record<LintKind, string> = {
  'caution': 'Keep this out of the email',
  'ai-sentence': 'Nobody talks like this',
  'capability-recital': 'Reads like a pitch',
  'no-agenda': '"No agenda" says the opposite',
  'yes-no-close': 'A question they can answer no to',
  'stale-date': 'A date that has passed',
  'stale-event': 'An old event as the opener',
}

export const ORIGIN_WORDS: Record<PartOrigin, string> = { model: 'drafted for you', override: 'your words' }

/** A shape on the stage that can be read and folded into the draft (REQ-103): everything but a person, a rep or the account. */
export function inspectable(kind: GraphNode['kind']): boolean {
  return kind === 'pursuit' || kind === 'thread' || kind === 'meeting' || kind === 'signal' || kind === 'finding' || kind === 'prior-work' || kind === 'note'
}

/** What a shape says about itself, in one line, for its card and for the prompt. */
export function shapeLine(n: GraphNode, fmt: (iso: string) => string): string {
  switch (n.kind) {
    case 'pursuit': return `${n.stage ?? 'pursuit'}${n.amount ? ` · ${n.amount.toLocaleString()}` : ''}${n.lastAt ? ` · last activity ${fmt(n.lastAt)}` : ''}`
    case 'thread': return `${n.direction === 'own' ? 'you wrote last' : 'they wrote last'} · ${fmt(n.lastAt)}${n.unansweredFollowUps ? ` · ${n.unansweredFollowUps} of yours unanswered` : ''}`
    case 'meeting': return `${fmt(n.at)} · ${n.attendees.length} in the room`
    case 'signal': return `${n.signalType} · ${fmt(n.at)}`
    case 'finding': return n.text
    case 'prior-work': return n.why
    case 'note': return n.at ? `Your note · ${fmt(n.at)}` : 'Your note'
    default: return ''
  }
}

/** The words put into the conversation box when a shape is incorporated (REQ-103): the fact, its date and source, and
 *  the ask — the rep reads it, edits it, and sends it; nothing is sent for them. */
export function incorporatePrompt(n: GraphNode, fmt: (iso: string) => string): string {
  // a note is the rep's own words already: the ask is to use them, not to cite them
  if (n.kind === 'note') return `Use what I wrote in my note '${n.label}'${n.at ? ` on ${fmt(n.at)}` : ''}: ${n.text ?? ''}`
  const kind = n.kind === 'prior-work' ? 'prior work' : n.kind === 'thread' ? 'thread' : n.kind
  const prov = 'provenance' in n && n.provenance ? ` (${provenanceWords(n.provenance, fmt)})` : ''
  const line = shapeLine(n, fmt)
  return `Incorporate into the draft: the ${kind} "${n.label}"${line ? ` — ${line}` : ''}${prov}. Use it where it strengthens the message, in my voice, and say where it came from.`
}

/** A model id as the crafting table names it: claude-fable-5-1 → "Fable 5.1", claude-cli:claude-sonnet-5 → "Sonnet 5",
 *  claude-haiku-4-5-20251001 → "Haiku 4.5". An id in no known shape is shown as it is; none is "no model". */
export function modelWords(id?: string | null): string {
  if (!id) return 'no model'
  const raw = id.replace(/^claude-cli:/, '').replace(/^claude-/, '')
  const m = raw.match(/^(fable|opus|sonnet|haiku)-(\d+)(?:-(\d{1,2}))?(?:-\d{6,})?$/)
  if (!m) return raw
  return `${m[1][0].toUpperCase()}${m[1].slice(1)} ${m[2]}${m[3] ? `.${m[3]}` : ''}`
}

/** A reason code, read the way the tag shows it: "hook · job change", "ask · two dates and a format", "insight · activate connections". */
export function reasonCodeWords(part: DraftPartKind, code?: string | null): string {
  const head = part === 'signoff' ? 'sign-off' : part
  const r = readReasonCode(code)
  return r ? `${head} · ${r.tag}` : head
}

/** "Why this is here", one line, from the reason code; nothing when there is no code (the subject, the sign-off). */
export function reasonLine(code?: string | null): string {
  return readReasonCode(code)?.line ?? ''
}

// The reason codes the API writes: hook:return-to · hook:referral · hook:signal:<signal> · hook:reason:<reasonKind> ·
// tactic:<key> · reason:<reasonKind> · ask:<askShape>. Unknown codes still read: their last word, spaces for dashes.
function readReasonCode(code?: string | null): { tag: string; line: string } | null {
  if (!code) return null
  const bits = code.split(':').filter(Boolean)
  if (bits.length === 0) return null
  const last = bits[bits.length - 1]!
  const plain = last.replace(/-/g, ' ')
  const known = (ASK_WORDS as Record<string, string>)[last] ?? plain
  switch (bits[0]) {
    case 'hook':
      if (bits[1] === 'return-to') return { tag: 'picking up where you left off', line: 'The opener picks up the last thing between you.' }
      if (bits[1] === 'referral') return { tag: 'a referral', line: 'The opener rests on the introduction you were given.' }
      if (bits[1] === 'signal') return { tag: `signal: ${plain}`, line: `The opener rests on a signal in your mail: ${plain}.` }
      if (bits[1] === 'reason') return { tag: known, line: `The opener rests on the reason for writing: ${known}.` }
      return { tag: plain, line: `The opener rests on ${plain}.` }
    case 'tactic': return { tag: `play: ${plain}`, line: `Here because the play "${plain}" calls for it.` }
    case 'reason': return { tag: known, line: `Here because of the reason for writing: ${known}.` }
    case 'ask': return { tag: known, line: `The ask is shaped as ${known}.` }
    case 'voice': return { tag: `voice: ${plain}`, line: `Your voice: ${plain}.` }
    default: return { tag: plain, line: `Here because: ${plain}.` }
  }
}

/** The whole draft's text from its parts, the way the API joins it: one line per part, subject first. */
export function partsToText(parts: DraftPart[]): string {
  return parts.map((p) => p.text).join('\n')
}

/** Which lint flags land in which part: a flag's span is an offset into the joined text. */
export function lintByPart(parts: DraftPart[], lint: LintFlag[]): Map<number, { flag: LintFlag; span?: [number, number] }[]> {
  const out = new Map<number, { flag: LintFlag; span?: [number, number] }[]>()
  for (const f of lint) {
    if (!f.span) continue
    let start = 0
    for (let i = 0; i < parts.length; i++) {
      const len = parts[i].text.length
      if (f.span[0] >= start && f.span[0] < start + len) {
        const list = out.get(i) ?? []
        list.push({ flag: f, span: [f.span[0] - start, Math.min(f.span[1] - start, len)] })
        out.set(i, list)
        break
      }
      start += len + 1
    }
  }
  return out
}

export const ASK_WORDS: Record<AskShape, string> = {
  'two-dates-plus-format': 'two dates and a format',
  'one-line-question': 'one question, no meeting',
  'owed-intro': 'the introduction you owe',
  'congratulation': 'a congratulation, no ask',
  'none': 'no ask',
}

export const CURRENCY_WORDS: Record<CurrencyState, string> = {
  'current': 'still there', 'likely-moved': 'likely moved', 'moved': 'moved', 'unknown': 'not confirmed',
}

export const SOURCE_WORDS: Record<Provenance['source'], string> = {
  'own-mail': 'your own mail', calendar: 'your calendar', import: 'your file', 'company-site': 'their website', filing: 'a filing',
  press: 'the press', aggregator: 'a data aggregator', research: 'research', said: 'you said so', scout: 'read by Scout',
  onenote: 'your OneNote',
}

export function provenanceWords(p: Provenance | undefined, formatDate: (iso: string) => string): string {
  if (!p) return ''
  const bits = [SOURCE_WORDS[p.source] ?? p.source, p.at ? formatDate(p.at) : null, p.note ?? null].filter(Boolean)
  return bits.join(' · ')
}

/** A short line for a reason the picker gives, unchanged; and the vetoes in danger tone are the caller's. */
export function scoreWidth(score: number): number {
  return Math.max(4, Math.min(100, Math.round(score * 100)))
}

/** The nodes that hang off one person: their meetings, threads, signals, findings, prior work — by the edges about them. */
export function satellitesOf(personId: string, nodes: GraphNode[], edges: GraphEdge[]): GraphNode[] {
  const about = new Set<string>()
  for (const e of edges) {
    if (e.from === personId) about.add(e.to)
    if (e.to === personId) about.add(e.from)
  }
  return nodes.filter((n) => about.has(n.id) && (n.kind === 'meeting' || n.kind === 'thread' || n.kind === 'signal' || n.kind === 'finding' || n.kind === 'prior-work' || n.kind === 'pursuit'))
}

/** The rep's own message count that went unanswered, in words a rep would use. */
export function silenceWords(silences: number): string {
  if (silences <= 0) return ''
  if (silences === 1) return 'one message of yours unanswered'
  return `${silences} messages of yours unanswered${silences >= 3 ? ' — change the ask, not the frequency' : ''}`
}

/** The text of a draft split at a lint flag's span into [before, flagged, after]; the whole text when no span. */
export function splitAt(text: string, span?: [number, number]): [string, string, string] {
  if (!span) return [text, '', '']
  const [a, b] = span
  if (a < 0 || b > text.length || a >= b) return [text, '', '']
  return [text.slice(0, a), text.slice(a, b), text.slice(b)]
}
